import React from "react";
import { Box, Stack, Typography, Avatar, Chip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { useCachedAvatar } from "../../hooks/useCachedAvatar";

/**
 * MagazineCollectionCard
 * 매거진 스타일의 컬렉션 카드 - CoverStoryCard와 동일한 스타일
 * @param {object} props
 * @param {object} props.collection - 컬렉션 데이터
 * @param {object} [props.user] - 사용자 메타 (expects id or profile.id)
 * @param {boolean} [props.isMobile]
 * @param {function} [props.onClick] - 클릭 핸들러 (없으면 기본 navigate 사용)
 */
const MagazineCollectionCard = ({ collection, user, isMobile, size = "default", onClick, categoryLabels = [] }) => {
	const theme = useTheme();
	const navigate = useNavigate();
	const username = user?.profile?.username || user?.profile?.name || "";
	const userId = String(user?.id || user?.user_id || user?.profile?.id || "");
	const fallbackAvatar =
		user?.cachedAvatarURL || 
		collection?.profileImage || 
		collection?.profile?.threads_profile_picture_url || 
		null;
	const { imageSrc } = useCachedAvatar(userId, fallbackAvatar, 160);
	const title = collection?.title || `Collection ${collection?.id}`;
	const description = collection?.description || "";
	const tags = Array.isArray(collection?.tags) ? collection.tags : [];
	const labels = Array.isArray(categoryLabels) ? categoryLabels : tags;

	// Size rules aligned with CardCollectionBook
	const isLarge = !isMobile && size === "large";
	const isSmall = size === "small";
	const cardHeight = isSmall ? "160px" : isMobile ? 196 : 440;
	const titleVariant = isMobile
		? "body2"
		: isLarge
		? "h4"
		: isSmall
		? "h6"
		: "h5";
	const descriptionVariant = isMobile
		? "body2"
		: isLarge
		? "body1"
		: isSmall
		? "body2"
		: "body1";
	const titleHeight = isSmall ? "auto" : isMobile ? 52 : isLarge ? 72 : 72;
	const descriptionHeight = isSmall ? "0px" : isMobile ? "40px" : "58px";

	return (
		<Stack 
			spacing={isMobile ? 2 : 0} 
			sx={{ 
				width: "100%", 
				py: isMobile ? 3 : 5, 
				px: isMobile ? 2 : '32px',
				height: cardHeight,
				minHeight: isSmall ? "160px" : isMobile ? "auto" : cardHeight,
				display: 'flex',
				flexDirection: 'column',
				justifyContent: 'space-between',
				transition: "background-color 0.2s ease-in-out, color 0.2s ease-in-out",
				cursor: "pointer",
				"&:hover": {
					backgroundColor: theme.palette.primary.main,
					"& *": {
						color: theme.palette.background.paper + " !important",
					},
				},
			}}
			onClick={() => {
				if (onClick) {
					onClick(collection);
				} else if (collection?.id) {
					navigate(`/collection/${collection.id}`);
				}
			}}
		>
			<Box sx={{ minWidth: 0 }}>
				<Box sx={{ textDecoration: "none" }}>
					<Typography
						variant={titleVariant}
						sx={{
							fontFamily:
								'"source-han-serif-korean", "Noto Serif KR", "Noto Serif CJK KR", "Source Han Serif KR", "Apple SD Gothic Neo", "Malgun Gothic", "맑은 고딕", serif',
							lineHeight: isMobile ? 1.55 : isLarge ? 1.7 : 1.55,
							height: titleHeight,
							overflow: "hidden",
							wordBreak: "keep-all",
							overflowWrap: "anywhere",
							textOverflow: "ellipsis",
							minWidth: 0,
							fontWeight: 900,
							color: theme.palette.text.primary,
							fontSize: isMobile ? "1.1rem" : undefined,
							mb: isMobile ? 2 : 3,
						}}
					>
						{title}
					</Typography>
					{!isMobile && !isSmall && (
						<Typography
							variant={descriptionVariant}
							sx={{
								height: descriptionHeight,
								overflow: "hidden",
								wordBreak: "break-word",
								overflowWrap: "anywhere",
								display: "-webkit-box",
								WebkitLineClamp: 2,
								WebkitBoxOrient: "vertical",
								minWidth: 0,
								color: theme.palette.text.primary,
							}}
						>
							{description}
						</Typography>
					)}

					{/* Tags / Category chips */}
					{!isSmall && labels.length > 0 && (
						<Box
							sx={{
								display: "flex",
								flexWrap: "wrap",
								gap: 0.5,
								mt: "24px !important",
								ml: "-4px !important",
						}}
						>
							{(isMobile ? labels.slice(0, 1) : labels).map((label, i) => (
								<Chip key={String(label) + i} label={label} size={isMobile ? "small" : "medium"} variant="outlined" />
							))}
						</Box>
					)}
				</Box>
			</Box>
			{/* Writer row pinned to bottom using space-between */}
			<Box
				sx={{
					display: "flex",
					boxSizing: "border-box",
				}}
			>
				<Stack
					direction="row"
					spacing={isMobile ? 1 : 1.5}
					alignItems="center"
					sx={{ minWidth: 0 }}
				>
					<Avatar
						src={imageSrc || undefined}
						sx={{ width: isMobile ? 32 : 44, height: isMobile ? 32 : 44 }}
					>
						{username ? username.charAt(0).toUpperCase() : "?"}
					</Avatar>
					<Typography
						variant={isMobile ? "caption" : "body1"}
						sx={{ 
							fontWeight: 700,
							fontSize: isMobile ? "0.75rem" : undefined,
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
							maxWidth: "100%",
						}}
					>
						@{username}
					</Typography>
				</Stack>
			</Box>
		</Stack>
	);
};

export default MagazineCollectionCard;